import type { SpeakerRole } from "@shared/ocenaTypy.js";
import { ApiError } from "./api.js";

/**
 * Robocze zmiany w podziale tekstu (status SPLIT_REVIEW) i ich wyslanie przy zatwierdzeniu.
 *
 * Do zatwierdzenia NIC nie idzie na serwer: zmiany mowcy przy wypowiedzi, nazw i rol leza tylko
 * w stanie ekranu, a "Zatwierdz i oceń" wysyla je razem w jednej transakcji. Dzieki temu opuszczenie
 * strony nie zostawia polowicznego podzialu. Funkcje sa czyste i niczego nie mutuja: widok trzyma
 * wynik w stanie Reacta.
 *
 * Tu leza tez nazwy rol i zasady nazw mowcow wspolne z oknem zmiany nazwy przy zwyklej analizie.
 */

/** Najdluzsza nazwa mowcy. LUSTRO src/services/wejscieSesji.ts (MAX_ZNAKOW_NAZWY_MOWCY); rozstrzyga serwer. */
export const MAX_ZNAKOW_NAZWY_MOWCY = 60;

export const ROLE_DO_WYBORU: readonly { wartosc: SpeakerRole; etykieta: string }[] = [
  { wartosc: "SPRZEDAWCA", etykieta: "Sprzedawca" },
  { wartosc: "KLIENT", etykieta: "Klient" },
  { wartosc: "INNY", etykieta: "Inny" },
];

export function etykietaRoli(rola: string): string {
  return ROLE_DO_WYBORU.find((r) => r.wartosc === rola)?.etykieta ?? "Inny";
}

/** Rola z bazy jest zwyklym tekstem; nieznana wartosc traktujemy jak INNY (tak robi serwer). */
export function rolaMowcy(rola: string): SpeakerRole {
  return ROLE_DO_WYBORU.find((r) => r.wartosc === rola)?.wartosc ?? "INNY";
}

/** Biale znaki zwijane do spacji, brzegi przyciete: tak samo jak na serwerze. */
export function normalizujNazwe(nazwa: string): string {
  return nazwa.replace(/[\s\p{Cc}]+/gu, " ").trim();
}

/** Najmniejszy ksztalt mowcy, jakiego potrzebuja te funkcje (pasuje do MowcaDto). */
export interface MowcaDoPodzialu {
  id: string;
  label: string;
  displayName: string | null;
  role: string;
}

/** Najmniejszy ksztalt wypowiedzi (pasuje do UtteranceDto). */
export interface WypowiedzDoPodzialu {
  index: number;
  speakerId: string;
}

/** Zmiana jednego mowcy: tekst z pola nazwy (pusty znaczy "wroc do etykiety") i/lub nowa rola. */
export interface ZmianaMowcy {
  nazwa?: string;
  rola?: SpeakerRole;
}

export interface ZmianyPodzialu {
  /** Numer wypowiedzi (od 0) -> id mowcy, ale tylko tam, gdzie rozni sie od stanu z serwera. */
  przypisania: ReadonlyMap<number, string>;
  /** Id mowcy -> zmiany nazwy i roli. */
  mowcy: ReadonlyMap<string, ZmianaMowcy>;
}

export const BEZ_ZMIAN: ZmianyPodzialu = { przypisania: new Map(), mowcy: new Map() };

export function czyJestZmiana(zmiany: ZmianyPodzialu): boolean {
  return zmiany.przypisania.size > 0 || zmiany.mowcy.size > 0;
}

/** Mowca wypowiedzi po uwzglednieniu zmian. */
export function mowcaWypowiedzi(u: WypowiedzDoPodzialu, zmiany: ZmianyPodzialu): string {
  return zmiany.przypisania.get(u.index) ?? u.speakerId;
}

/** Przypisuje wypowiedz innemu mowcy. Powrot do mowcy z serwera kasuje wpis (brak zmiany to brak wpisu). */
export function przypiszWypowiedz(
  zmiany: ZmianyPodzialu,
  u: WypowiedzDoPodzialu,
  idMowcy: string,
): ZmianyPodzialu {
  const przypisania = new Map(zmiany.przypisania);
  if (idMowcy === u.speakerId) przypisania.delete(u.index);
  else przypisania.set(u.index, idMowcy);
  return { ...zmiany, przypisania };
}

function ustawZmianeMowcy(
  zmiany: ZmianyPodzialu,
  mowca: MowcaDoPodzialu,
  zmiana: ZmianaMowcy,
): ZmianyPodzialu {
  const mowcy = new Map(zmiany.mowcy);
  const polaczona: ZmianaMowcy = { ...mowcy.get(mowca.id), ...zmiana };
  // Wpis, ktory niczego nie zmienia wzgledem serwera, znika.
  const nazwaBezZmiany =
    polaczona.nazwa === undefined || normalizujNazwe(polaczona.nazwa) === (mowca.displayName ?? "");
  const rolaBezZmiany = polaczona.rola === undefined || polaczona.rola === rolaMowcy(mowca.role);
  if (nazwaBezZmiany && rolaBezZmiany) mowcy.delete(mowca.id);
  else mowcy.set(mowca.id, polaczona);
  return { ...zmiany, mowcy };
}

export function ustawNazweMowcy(zmiany: ZmianyPodzialu, mowca: MowcaDoPodzialu, nazwa: string): ZmianyPodzialu {
  return ustawZmianeMowcy(zmiany, mowca, { nazwa });
}

export function ustawRoleMowcy(zmiany: ZmianyPodzialu, mowca: MowcaDoPodzialu, rola: SpeakerRole): ZmianyPodzialu {
  return ustawZmianeMowcy(zmiany, mowca, { rola });
}

/** Tekst w polu nazwy: to, co wpisano, a bez zmiany - nazwa wlasna z serwera (albo pusto). */
export function tekstPolaNazwy(mowca: MowcaDoPodzialu, zmiany: ZmianyPodzialu): string {
  return zmiany.mowcy.get(mowca.id)?.nazwa ?? mowca.displayName ?? "";
}

export function rolaPoZmianach(mowca: MowcaDoPodzialu, zmiany: ZmianyPodzialu): SpeakerRole {
  return zmiany.mowcy.get(mowca.id)?.rola ?? rolaMowcy(mowca.role);
}

/** Nazwa do pokazania: wpisana (po normalizacji), a gdy pusta - etykieta ("Mówca 1"). */
export function nazwaPoZmianach(mowca: MowcaDoPodzialu, zmiany: ZmianyPodzialu): string {
  const nazwa = normalizujNazwe(tekstPolaNazwy(mowca, zmiany));
  return nazwa.length > 0 ? nazwa : mowca.label;
}

/** Ile wypowiedzi ma kazdy mowca po zmianach (mowca bez wypowiedzi ma 0). */
export function liczbyWypowiedzi(
  mowcy: readonly MowcaDoPodzialu[],
  wypowiedzi: readonly WypowiedzDoPodzialu[],
  zmiany: ZmianyPodzialu,
): Map<string, number> {
  const liczby = new Map(mowcy.map((m) => [m.id, 0]));
  for (const u of wypowiedzi) {
    const id = mowcaWypowiedzi(u, zmiany);
    liczby.set(id, (liczby.get(id) ?? 0) + 1);
  }
  return liczby;
}

export type BladNazwy = "za-dluga" | "zajeta";

/**
 * Bledy nazw po zmianach: za dluga i taka sama jak u innego mowcy (bez rozrozniania wielkosci
 * liter). Serwer odrzuca to samo (400 nazwa_za_dluga, 409 nazwa_zajeta), ale chcemy pokazac to przy
 * polu, zanim cokolwiek poleci. Mowca bez wypowiedzi nie liczy sie do duplikatow: serwer usunie go
 * przy zatwierdzeniu, a zostawiona jego nazwa nie moze blokowac zatwierdzenia.
 */
export function bledyNazw(
  mowcy: readonly MowcaDoPodzialu[],
  zmiany: ZmianyPodzialu,
  liczby: ReadonlyMap<string, number>,
): Map<string, BladNazwy> {
  const bledy = new Map<string, BladNazwy>();
  const zajete = new Map<string, string>();

  for (const m of mowcy) {
    if (nazwaPoZmianach(m, zmiany).length > MAX_ZNAKOW_NAZWY_MOWCY) bledy.set(m.id, "za-dluga");
  }

  for (const m of mowcy) {
    if ((liczby.get(m.id) ?? 0) === 0) continue;
    const klucz = nazwaPoZmianach(m, zmiany).toLocaleLowerCase("pl");
    const pierwszy = zajete.get(klucz);
    if (pierwszy === undefined) {
      zajete.set(klucz, m.id);
      continue;
    }
    if (!bledy.has(m.id)) bledy.set(m.id, "zajeta");
    if (!bledy.has(pierwszy)) bledy.set(pierwszy, "zajeta");
  }
  return bledy;
}

export interface CialoZatwierdzenia {
  lensSlugs?: string[];
  wypowiedzi?: { index: number; speakerId: string }[];
  mowcy?: { id: string; displayName?: string | null; role?: SpeakerRole }[];
}

/** Cialo POST /podzial/zatwierdz: tylko to, co sie zmienilo, i wybor parametrow, gdy jest znany. */
export function zbudujZatwierdzenie(
  zmiany: ZmianyPodzialu,
  wybraneParametry: readonly string[] | null,
): CialoZatwierdzenia {
  const cialo: CialoZatwierdzenia = {};

  if (wybraneParametry !== null) cialo.lensSlugs = [...wybraneParametry];

  if (zmiany.przypisania.size > 0) {
    cialo.wypowiedzi = [...zmiany.przypisania]
      .map(([index, speakerId]) => ({ index, speakerId }))
      .sort((a, b) => a.index - b.index);
  }

  if (zmiany.mowcy.size > 0) {
    cialo.mowcy = [...zmiany.mowcy].map(([id, z]) => {
      const wpis: { id: string; displayName?: string | null; role?: SpeakerRole } = { id };
      if (z.nazwa !== undefined) {
        const nazwa = normalizujNazwe(z.nazwa);
        wpis.displayName = nazwa.length > 0 ? nazwa : null;
      }
      if (z.rola !== undefined) wpis.role = z.rola;
      return wpis;
    });
  }
  return cialo;
}

/** Zdanie o tym, co zrobi zatwierdzenie, do podpowiedzi pod przyciskiem. */
export function podsumowanieZmian(zmiany: ZmianyPodzialu): string {
  const przypisania = zmiany.przypisania.size;
  const mowcy = zmiany.mowcy.size;
  if (przypisania === 0 && mowcy === 0) return "Nie wprowadzono żadnych poprawek.";
  const czesci: string[] = [];
  if (przypisania > 0) czesci.push(`zmieniono mówcę przy ${przypisania} ${przypisania === 1 ? "wypowiedzi" : "wypowiedziach"}`);
  if (mowcy > 0) czesci.push(`poprawiono nazwę lub rolę ${mowcy === 1 ? "jednego mówcy" : `${mowcy} mówców`}`);
  const zdanie = czesci.join(", ");
  return `Poprawki: ${zdanie}.`;
}

// ---------------------------------------------------------------------------
// Bledy serwera
// ---------------------------------------------------------------------------

export interface BladZatwierdzenia {
  /** Przy czym stoi komunikat: wybor parametrow, lista mowcow albo przy przycisku. */
  pole: "soczewki" | "mowcy" | "ogolny";
  tekst: string;
  /** Stan sesji na serwerze jest inny niz na ekranie: trzeba ja pobrac jeszcze raz. */
  odswiezSesje?: boolean;
  /** Wybor parametrow jest nieaktualny: trzeba pobrac liste jeszcze raz. */
  odswiezParametry?: boolean;
}

/** Kod bledu z POST /podzial/zatwierdz na zdanie (S-1: po ludzku, nigdy sam kod). */
export function bladZatwierdzenia(err: unknown): BladZatwierdzenia {
  if (!(err instanceof ApiError)) {
    return { pole: "ogolny", tekst: "Nie udało się zatwierdzić podziału. Sprawdź połączenie i spróbuj ponownie." };
  }

  switch (err.code) {
    case "podzial_juz_zatwierdzony":
    case "zly_status":
      return { pole: "ogolny", tekst: "Ten podział został już zatwierdzony. Odświeżamy stronę.", odswiezSesje: true };
    case "nazwa_zajeta": {
      const nazwa = typeof err.szczegoly.nazwa === "string" ? err.szczegoly.nazwa : null;
      return {
        pole: "mowcy",
        tekst: nazwa
          ? `Dwóch mówców ma nazwę ${nazwa}. Nadaj im różne nazwy.`
          : "Dwóch mówców ma taką samą nazwę. Nadaj im różne nazwy.",
      };
    }
    case "nazwa_za_dluga":
      return { pole: "mowcy", tekst: `Nazwa mówcy może mieć najwyżej ${MAX_ZNAKOW_NAZWY_MOWCY} znaków.` };
    case "zle_pole_nazwy":
      return { pole: "mowcy", tekst: "Nazwa mówcy ma niepoprawną postać. Wpisz ją jeszcze raz." };
    case "zla_rola":
      return { pole: "mowcy", tekst: "Wybierz rolę mówcy z listy." };
    case "brak_soczewek":
    case "zle_pole_soczewek":
      return { pole: "soczewki", tekst: "Zaznacz co najmniej jeden parametr oceny." };
    case "nieznana_soczewka":
    case "soczewka_wylaczona":
      return {
        pole: "soczewki",
        tekst: "Jeden z wybranych parametrów nie jest już dostępny. Odświeżyliśmy listę - zaznacz parametry jeszcze raz.",
        odswiezParametry: true,
      };
    case "model_niedostepny":
      return {
        pole: "ogolny",
        tekst:
          "Nie możemy teraz ocenić tekstu, bo serwer nie ma połączenia z modelem oceny. Spróbuj ponownie później albo daj nam znać.",
      };
    case "nieznana_wypowiedz":
    case "nieznany_mowca":
    case "zla_poprawka_wypowiedzi":
    case "powtorzona_wypowiedz":
    case "zle_pole_wypowiedzi":
    case "zla_poprawka_mowcy":
    case "powtorzony_mowca":
    case "zle_pole_mowcow":
      return {
        pole: "ogolny",
        tekst: `Podział na ekranie nie zgadza się z tym, co jest na serwerze. Odśwież stronę i spróbuj ponownie (${err.code}).`,
      };
    case "nie_ma_takiej_sesji":
      return { pole: "ogolny", tekst: "Nie ma już tej analizy. Wróć do listy analiz." };
    default:
      return { pole: "ogolny", tekst: `Nie udało się zatwierdzić podziału (${err.code}).` };
  }
}

export interface BladZmianyMowcy {
  pole: "nazwa" | "rola" | "ogolny";
  tekst: string;
}

/** Kod bledu z PATCH /speakers/:id na zdanie przy polu okna zmiany nazwy. */
export function bladZmianyMowcy(err: unknown): BladZmianyMowcy {
  if (!(err instanceof ApiError)) {
    return { pole: "ogolny", tekst: "Nie udało się zapisać zmiany. Sprawdź połączenie i spróbuj ponownie." };
  }

  switch (err.code) {
    case "nazwa_zajeta": {
      const nazwa = typeof err.szczegoly.nazwa === "string" ? err.szczegoly.nazwa : null;
      return {
        pole: "nazwa",
        tekst: nazwa ? `Inny mówca ma już nazwę ${nazwa}. Wybierz inną.` : "Inny mówca ma już taką nazwę. Wybierz inną.",
      };
    }
    case "nazwa_za_dluga":
      return { pole: "nazwa", tekst: `Nazwa może mieć najwyżej ${MAX_ZNAKOW_NAZWY_MOWCY} znaków.` };
    case "zle_pole_nazwy":
      return { pole: "nazwa", tekst: "Nazwa ma niepoprawną postać. Wpisz ją jeszcze raz." };
    case "zla_rola":
      return { pole: "rola", tekst: "Wybierz rolę z listy." };
    case "nie_ma_takiego_mowcy":
    case "nie_ma_takiej_sesji":
      return { pole: "ogolny", tekst: "Nie ma już tego mówcy. Odśwież stronę i spróbuj ponownie." };
    default:
      return { pole: "ogolny", tekst: `Nie udało się zapisać zmiany (${err.code}).` };
  }
}
